import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTableModule } from 'ng-zorro-antd/table';
import { AdminSeatsService } from '../services/admin-seats.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-seats-rbac-admin',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    NzCardModule,
    NzTableModule,
    NzButtonModule,
    NzInputModule,
    NzSwitchModule,
  ],
  templateUrl: './seats-rbac-admin.component.html',
  styleUrl: './seats-rbac-admin.component.less'
})
export class SeatsRbacAdminComponent implements OnInit {
  plans: any[] = [];
  companies: any[] = [];
  loading = false;
  search = '';

  constructor(
    private adminSeatsService: AdminSeatsService,
    private message: NzMessageService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.adminSeatsService.getPlans().subscribe({
      next: (plans) => {
        this.plans = plans || [];
      },
      error: () => {
        this.plans = [];
      }
    });

    this.adminSeatsService.getCompanies().subscribe({
      next: (companies) => {
        this.companies = (companies || []).map((company: any) => ({
          ...company,
          allowedDomainsText: Array.isArray(company.allowedDomains) ? company.allowedDomains.join(', ') : '',
          allowedTenantIdsText: Array.isArray(company.allowedTenantIds) ? company.allowedTenantIds.join(', ') : '',
        }));
        this.loading = false;
      },
      error: () => {
        this.companies = [];
        this.loading = false;
      }
    });
  }

  savePlan(plan: any): void {
    this.adminSeatsService.updatePlan(plan.planKey, Number(plan.defaultMaxUsers)).subscribe({
      next: () => this.message.success(this.translate.instant('ADMIN.SEATS_RBAC.SAVED')),
      error: () => this.message.error(this.translate.instant('ERRORS.NOT_AUTHORIZED')),
    });
  }

  async saveCompany(company: any): Promise<void> {
    const seatLimitValue = company.seatLimit === '' || company.seatLimit === null || typeof company.seatLimit === 'undefined'
      ? null
      : Number(company.seatLimit);

    try {
      await firstValueFrom(this.adminSeatsService.updateCompanySeats(company._id, {
        seatLimit: seatLimitValue,
        seatEnforcementEnabled: !!company.seatEnforcementEnabled,
      }));

      await firstValueFrom(this.adminSeatsService.updateCompanySso(company._id, {
        ssoAutoJoinEnabled: !!company.ssoAutoJoinEnabled,
        allowedDomains: (company.allowedDomainsText || '')
          .split(',')
          .map((value: string) => value.trim().toLowerCase())
          .filter((value: string) => !!value),
        allowedTenantIds: (company.allowedTenantIdsText || '')
          .split(',')
          .map((value: string) => value.trim().toLowerCase())
          .filter((value: string) => !!value),
      }));

      this.message.success(this.translate.instant('ADMIN.SEATS_RBAC.SAVED'));
    } catch (error: any) {
      const code = error?.error?.code;
      const key = error?.error?.messageKey;
      if (code === 'CONFLICT' || key === 'ERRORS.CONFLICT_RETRY') {
        this.message.error(this.translate.instant('ERRORS.CONFLICT_RETRY'));
        return;
      }
      this.message.error(this.translate.instant('ERRORS.NOT_AUTHORIZED'));
    }
  }

  get filteredCompanies(): any[] {
    const term = this.search.trim().toLowerCase();
    if (!term) {
      return this.companies;
    }
    return this.companies.filter((company) =>
      `${company.name || ''} ${company.domain || ''}`.toLowerCase().includes(term)
    );
  }
}
